import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Navigation,
  Paper,
  Stack,
  Typography,
  Tooltip,
  type NavItem,
} from '@locus-taxy/encore-mui';
import { PageHeader } from '@locus-taxy/encore-ui-toolkit';
import { Fullscreen, FullscreenExit } from '@locus-taxy/encore-mui/icons';
import { DatabricksDashboard } from '@databricks/aibi-client';
import { PageContainer } from '@toolpad/core/PageContainer';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import {
  useLocation,
  useMatch,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  useEntitySearch,
  type EntitySearchRequest,
} from '../api/endpoints/useEntitySearch';
import {
  useAnalyticsDashboardToken,
  type AnalyticsDashboardTokenData,
} from '../api/endpoints/useAnalyticsDashboardToken';
import type { DashboardMetadata } from '../types/dashboardMetadata';
import { useClientId } from '../hooks/useClientId';
import { buildClientPrefixedPath } from '../utils/url';
import { RoutePath } from '../types/categories';
import { appendTeamsToHref, extractTeamsParam } from '../utils/teamsQueryParam';
import { getTestId } from '@locus-taxy/encore-utils';
import { useRouteContext } from '../hooks/useRouteContext';
import { TestIdAction } from '../utils/testIdUtils';

const DASHBOARD_PAGE_SIZE = 100;

interface DashboardAccessParameters {
  token: string | null;
  tokenType?: string | null;
}

// Fullscreen helpers to support cross-browser behavior
type VendorDocument = Document & {
  webkitFullscreenElement?: Element | null;
  mozFullScreenElement?: Element | null;
  msFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
  mozCancelFullScreen?: () => Promise<void> | void;
  msExitFullscreen?: () => Promise<void> | void;
};

type VendorHTMLElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
  mozRequestFullScreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => Promise<void> | void;
};

function getFullscreenElement(): Element | null {
  const vendorDoc = document as VendorDocument;
  return (
    document.fullscreenElement ||
    vendorDoc.webkitFullscreenElement ||
    vendorDoc.mozFullScreenElement ||
    vendorDoc.msFullscreenElement ||
    null
  );
}

function requestElementFullscreen(el: HTMLElement) {
  const vendorEl = el as VendorHTMLElement;
  (
    vendorEl.requestFullscreen ||
    vendorEl.webkitRequestFullscreen ||
    vendorEl.mozRequestFullScreen ||
    vendorEl.msRequestFullscreen
  )?.call(vendorEl);
}

function exitDocumentFullscreen() {
  const vendorDoc = document as VendorDocument;
  (
    document.exitFullscreen ||
    vendorDoc.webkitExitFullscreen ||
    vendorDoc.mozCancelFullScreen ||
    vendorDoc.msExitFullscreen
  )?.call(document);
}

export default function AnalyticsPage() {
  const { t } = useTranslation();
  const clientId = useClientId();
  const navigate = useNavigate();
  const location = useLocation();
  const { entity, page } = useRouteContext();
  const [searchParams] = useSearchParams();
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(
    null
  );
  const [embedError, setEmbedError] = useState<string | null>(null);
  const dashboardContainerRef = useRef<HTMLDivElement | null>(null);
  const dashboardInstanceRef = useRef<DatabricksDashboard | null>(null);
  const previousDashboardIdRef = useRef<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const searchRequest = useMemo<EntitySearchRequest>(
    () => ({
      request: {
        sort: [{ field: 'entityData.description', direction: 'ASC' }],
        pagination: { page: 1, size: DASHBOARD_PAGE_SIZE },
      },
    }),
    []
  );

  const { data, isLoading, isError } = useEntitySearch<DashboardMetadata>(
    clientId,
    'dashboardMetadata',
    searchRequest,
    { enabled: Boolean(clientId) }
  );

  const dashboards = useMemo<DashboardMetadata[]>(() => {
    const records = data?.data?.content ?? [];
    return records
      .map(record => record.entityData)
      .filter((entity): entity is DashboardMetadata => Boolean(entity));
  }, [data?.data?.content]);

  // Read dashboardId from URL: /clientId/:clientId/analytics/dashboards/:dashboardId
  const dashboardMatch = useMatch(
    '/clientId/:clientId/analytics/dashboards/:dashboardId'
  );
  const urlDashboardId =
    (dashboardMatch?.params?.dashboardId as string) || null;
  const teamsParam = extractTeamsParam(searchParams);

  const withTeamsParam = useCallback(
    (path: string) => {
      return appendTeamsToHref(path, teamsParam, { skipExternal: false });
    },
    [teamsParam]
  );

  useEffect(() => {
    if (!dashboards.length) {
      setSelectedDashboardId(null);
      return;
    }

    // If URL has a dashboardId and it exists, sync selection to it
    if (urlDashboardId) {
      if (dashboards.some(d => d.id === urlDashboardId)) {
        setSelectedDashboardId(urlDashboardId);
        return;
      }
      // URL has invalid id; redirect to first available
      const firstId = dashboards[0]?.id;
      if (firstId) {
        navigate(
          withTeamsParam(
            buildClientPrefixedPath(
              `${RoutePath.ANALYTICS}/dashboards/${firstId}`,
              clientId
            )
          ),
          { replace: true }
        );
      }
      return;
    }

    // No dashboard in URL: redirect base /analytics to first dashboard
    const firstId = dashboards[0]?.id ?? null;
    if (firstId) {
      const analyticsHomePath = buildClientPrefixedPath(
        RoutePath.ANALYTICS,
        clientId
      );
      const pathname = location.pathname;
      if (
        pathname === analyticsHomePath ||
        pathname === `${analyticsHomePath}/`
      ) {
        navigate(
          withTeamsParam(
            buildClientPrefixedPath(
              `${RoutePath.ANALYTICS}/dashboards/${firstId}`,
              clientId
            )
          ),
          { replace: true }
        );
        setSelectedDashboardId(firstId);
        return;
      }
    }

    // Fallback: ensure selection is valid
    setSelectedDashboardId(prev => {
      if (prev && dashboards.some(d => d.id === prev)) {
        return prev;
      }
      return dashboards[0]?.id ?? null;
    });
  }, [
    dashboards,
    urlDashboardId,
    clientId,
    navigate,
    location.pathname,
    withTeamsParam,
  ]);

  const selectedDashboard = useMemo(
    () => dashboards.find(d => d.id === selectedDashboardId) ?? null,
    [dashboards, selectedDashboardId]
  );

  const activeDashboardContext = useMemo(
    () =>
      selectedDashboard
        ? {
            dashboardId: selectedDashboard.dashboardId,
            workspaceId: selectedDashboard.workspaceId,
            instanceUrl: selectedDashboard.instanceUrl,
          }
        : null,
    [selectedDashboard]
  );

  const {
    data: tokenResponse,
    isLoading: isTokenLoading,
    isError: isTokenError,
    refetch: refetchToken,
  } = useAnalyticsDashboardToken(clientId, selectedDashboard?.id, {
    enabled: Boolean(selectedDashboard?.id),
  });

  const resolveTokenDetails = useCallback(
    (
      payload: AnalyticsDashboardTokenData | null | undefined
    ): DashboardAccessParameters => {
      if (!payload) {
        return { token: null, tokenType: null };
      }

      const tokenCandidate =
        (typeof payload.access_token === 'string'
          ? payload.access_token
          : null) ??
        (typeof payload.accessToken === 'string'
          ? payload.accessToken
          : null) ??
        (typeof payload.token === 'string' ? payload.token : null) ??
        null;

      const tokenTypeCandidate =
        (typeof payload.token_type === 'string' ? payload.token_type : null) ??
        (typeof payload.tokenType === 'string' ? payload.tokenType : null) ??
        null;

      return {
        token: tokenCandidate,
        tokenType: tokenTypeCandidate,
      };
    },
    []
  );

  const tokenDetails = useMemo(
    () => resolveTokenDetails(tokenResponse?.data),
    [resolveTokenDetails, tokenResponse?.data]
  );

  const getNewToken = useCallback(async () => {
    const refreshed = await refetchToken();
    const nextPayload = refreshed.data?.data;
    const nextDetails = resolveTokenDetails(nextPayload);
    if (!nextDetails.token) {
      throw new Error('Failed to refresh analytics dashboard token');
    }
    return nextDetails.token;
  }, [refetchToken, resolveTokenDetails]);

  const destroyDashboardInstance = useCallback(() => {
    if (dashboardInstanceRef.current) {
      dashboardInstanceRef.current.destroy();
      dashboardInstanceRef.current = null;
    }
    if (dashboardContainerRef.current) {
      dashboardContainerRef.current.innerHTML = '';
    }
    previousDashboardIdRef.current = null;
  }, []);

  const navigationItems = useMemo<NavItem[]>(
    () =>
      dashboards.map(dashboard => {
        const labelText =
          dashboard.description || t('analytics.unnamedDashboard');
        const labelNode = (
          <Box
            component="span"
            data-testid={getTestId({
              action: TestIdAction.NAVIGATE,
              object: 'navigation-item',
              entity: dashboard.description ?? dashboard.id,
              page: 'analytics',
            })}
          >
            {labelText}
          </Box>
        );
        return { id: dashboard.id, label: labelNode as unknown as string };
      }),
    [dashboards, t]
  );

  const activeNavigationPath = useMemo(
    () => (selectedDashboardId ? [selectedDashboardId] : []),
    [selectedDashboardId]
  );

  const handleNavigationClick = useCallback(
    (path: string[]) => {
      const lastId = path[path.length - 1] ?? null;
      setEmbedError(null);
      if (lastId) {
        setSelectedDashboardId(lastId);
        navigate(
          buildClientPrefixedPath(
            `${RoutePath.ANALYTICS}/dashboards/${lastId}`,
            clientId
          )
        );
      }
    },
    [clientId, navigate]
  );

  useEffect(() => {
    const container = dashboardContainerRef.current;

    if (!container) {
      return;
    }

    if (!activeDashboardContext || !tokenDetails.token) {
      destroyDashboardInstance();
      if (embedError) {
        setEmbedError(null);
      }
      return;
    }

    if (embedError) {
      return;
    }

    const shouldReinitialize =
      !dashboardInstanceRef.current ||
      previousDashboardIdRef.current !== activeDashboardContext.dashboardId;

    if (!shouldReinitialize) {
      return;
    }

    destroyDashboardInstance();

    try {
      const dashboard = new DatabricksDashboard({
        workspaceId: activeDashboardContext.workspaceId,
        instanceUrl: activeDashboardContext.instanceUrl,
        container,
        dashboardId: activeDashboardContext.dashboardId,
        token: tokenDetails.token,
        getNewToken,
        colorScheme: 'light',
      });

      dashboard.initialize();
      dashboardInstanceRef.current = dashboard;
      previousDashboardIdRef.current = activeDashboardContext.dashboardId;
    } catch {
      destroyDashboardInstance();
      setEmbedError(t('analytics.embedError'));
    }
  }, [
    activeDashboardContext,
    destroyDashboardInstance,
    embedError,
    getNewToken,
    t,
    tokenDetails.token,
  ]);

  useEffect(
    () => () => {
      destroyDashboardInstance();
    },
    [destroyDashboardInstance]
  );

  const headerTitle = t('analytics.title');

  const handleBackClick = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(buildClientPrefixedPath('/', clientId), { replace: true });
  }, [clientId, navigate]);

  // Fullscreen state sync
  useEffect(() => {
    const update = () => {
      const current = getFullscreenElement();
      setIsFullscreen(Boolean(current));
    };
    document.addEventListener('fullscreenchange', update);
    document.addEventListener(
      'webkitfullscreenchange',
      update as EventListener
    );
    document.addEventListener('MSFullscreenChange', update as EventListener);
    return () => {
      document.removeEventListener('fullscreenchange', update);
      document.removeEventListener(
        'webkitfullscreenchange',
        update as EventListener
      );
      document.removeEventListener(
        'MSFullscreenChange',
        update as EventListener
      );
    };
  }, []);

  const handleToggleFullscreen = useCallback(() => {
    const container = dashboardContainerRef.current;
    if (!container) {
      return;
    }
    const current = getFullscreenElement();
    if (current) {
      exitDocumentFullscreen();
    } else {
      requestElementFullscreen(container);
    }
  }, []);

  const enterFsLabel = t('analytics.enterFullscreen', {
    defaultValue: 'Enter fullscreen',
  });
  const exitFsLabel = t('analytics.exitFullscreen', {
    defaultValue: 'Exit fullscreen',
  });

  const actionStack = useMemo(
    () => [
      {
        id: 'toggle-fullscreen',
        icon: (
          <Tooltip title={isFullscreen ? exitFsLabel : enterFsLabel}>
            {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
          </Tooltip>
        ),
        ariaLabel: isFullscreen ? exitFsLabel : enterFsLabel,
        onClick: handleToggleFullscreen,
        disabled: !selectedDashboard || isTokenLoading || Boolean(embedError),
      },
    ],
    [
      embedError,
      enterFsLabel,
      exitFsLabel,
      handleToggleFullscreen,
      isFullscreen,
      isTokenLoading,
      selectedDashboard,
    ]
  );

  const navigationContent = useMemo(() => {
    if (isLoading) {
      return (
        <Stack alignItems="center" justifyContent="center" height="100%" p={2}>
          <CircularProgress size={24} />
        </Stack>
      );
    }

    if (isError) {
      return (
        <Typography variant="body2" color="error" padding={2}>
          {t('analytics.errorLoading')}
        </Typography>
      );
    }

    if (!dashboards.length) {
      return (
        <Typography variant="body2" color="text.secondary" padding={2}>
          {t('analytics.noDashboards')}
        </Typography>
      );
    }

    return (
      <Navigation
        items={navigationItems}
        activePath={activeNavigationPath}
        onItemClick={handleNavigationClick}
      />
    );
  }, [
    activeNavigationPath,
    dashboards.length,
    handleNavigationClick,
    isError,
    isLoading,
    navigationItems,
    t,
  ]);

  const renderDashboardViewer = () => {
    if (!selectedDashboard) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          flex={1}
          p={3}
          textAlign="center"
        >
          <Typography variant="body1" color="text.secondary">
            {t('analytics.selectDashboardPrompt')}
          </Typography>
        </Stack>
      );
    }

    const dashboardLabel =
      selectedDashboard.description ?? selectedDashboard.id;

    if (isTokenLoading) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          flex={1}
          p={3}
          spacing={2}
          textAlign="center"
        >
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            {t('analytics.loadingDashboard')}
          </Typography>
        </Stack>
      );
    }

    if (isTokenError) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          flex={1}
          p={3}
          spacing={2}
          textAlign="center"
        >
          <Typography variant="body2" color="error">
            {t('analytics.tokenError')}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => refetchToken()}
            data-testid={getTestId({
              action: TestIdAction.RETRY,
              object: 'button',
              entity: dashboardLabel,
              page: 'analytics',
            })}
          >
            {t('analytics.retry')}
          </Button>
        </Stack>
      );
    }

    if (embedError) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          flex={1}
          p={3}
          spacing={2}
          textAlign="center"
        >
          <Typography variant="body2" color="error">
            {embedError}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setEmbedError(null);
              destroyDashboardInstance();
              refetchToken();
            }}
            data-testid={getTestId({
              action: TestIdAction.RETRY,
              object: 'button',
              entity: dashboardLabel,
              page: 'analytics',
            })}
          >
            {t('analytics.retry')}
          </Button>
        </Stack>
      );
    }

    if (!tokenDetails.token) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          flex={1}
          p={3}
          textAlign="center"
        >
          <Typography variant="body2" color="text.secondary">
            {t('analytics.tokenUnavailable')}
          </Typography>
        </Stack>
      );
    }

    return (
      <Box
        key={selectedDashboard.dashboardId}
        ref={dashboardContainerRef}
        width="100%"
        height="100%"
        flexGrow={1}
      />
    );
  };

  return (
    <Stack height="100%" overflow="hidden" my={0}>
      <PageHeader
        title={headerTitle}
        onBackClick={handleBackClick}
        actionStack={actionStack}
        routeContext={{
          entity: entity ?? 'analytics',
          page: page ?? 'dashboard',
        }}
      />
      <PageContainer
        data-testid={getTestId({
          action: TestIdAction.VIEW,
          object: 'analytics-container',
          entity: selectedDashboard?.description ?? selectedDashboard?.id,
          page: 'analytics',
        })}
        maxWidth={false}
        disableGutters
        slots={{
          header: () => null,
        }}
        sx={{
          '& >.MuiStack-root': {
            my: 0,
          },
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          flexGrow={1}
          gap={2}
          my={0}
        >
          <Paper sx={{ width: { xs: '100%', md: 280 } }}>
            <Stack direction="column" gap={2} p={2}>
              <Box flexGrow={1} minHeight={0} overflow="auto">
                {navigationContent}
              </Box>
            </Stack>
          </Paper>
          <Paper sx={{ flex: 1 }}>{renderDashboardViewer()}</Paper>
        </Stack>
      </PageContainer>
    </Stack>
  );
}
